import { useMemo, useRef, useState } from 'react';
import {
  Button, useCreateSuggestionContext, useGetList, useRecordContext, useTranslate,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import {
  Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  useTheme,
} from '@mui/material';
import { isEmail } from 'ra-core/dist/cjs/inference/assertions';
import { LIST_DEBOUNCE } from '../../../../config';
import { GrayAutocompleteArrayInputWithUpperLabel } from './GrayAutocompleteArrayInputWithUpperLabel';
import { GrayTextFieldWithUpperLabel } from '../fields/GrayTextFieldWithUpperLabel';

const CERTIFIED_USER_ID = 'certified_user';

/**
 * To be able to include not only users but also emails and the certified user, we need to have a common type that represents different types of choices.
 * For certified user and emails, ids are arbitrary but still must be included to conform to the type.
 */
type NotifiedPersonChoice =
  | { id: typeof CERTIFIED_USER_ID; type: 'certifiedUser' }
  | { id: string; type: 'user'; name: string }
  | { id: string; type: 'email'; email: string };

type NotifiedPeopleInputProps = {
  showCertifiedUserOption?: boolean;
  label: string;
  helperText?: string;
  isRequired?: boolean;
};

const toEmailChoice = (email: string): NotifiedPersonChoice => ({
  id: `email:${email}`,
  type: 'email',
  email,
});

const EmailCreateDialog = ({
  onAddChoice,
}: {
  onAddChoice: (choice: NotifiedPersonChoice) => void;
}) => {

  const translate = useTranslate();
  const theme = useTheme();
  const { onCancel, onCreate } = useCreateSuggestionContext();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>();

  const handleClose = () => {

    setEmail('');
    setError(undefined);
    onCancel();

  };

  const handleConfirm = () => {

    const trimmed = email.trim();
    if (!trimmed || !isEmail(trimmed)) {

      setError('general.text.invalid_email');
      return;

    }
    const choice = toEmailChoice(trimmed);
    onAddChoice(choice);
    onCreate(choice);
    setEmail('');
    setError(undefined);

  };

  return (
    <Dialog open onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {translate('general.text.add_email_title')}
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <GrayTextFieldWithUpperLabel
          fullWidth
          autoFocus
          value={email}
          onChange={(event) => {

            setEmail(event.target.value);
            setError(undefined);

          }}
          label={translate('general.fields.email')}
          error={Boolean(error)}
          helperText={false}
          onKeyDown={(event) => {

            if (event.key === 'Enter') {

              event.preventDefault();
              handleConfirm();

            }

          }}
        />
        {error && (
          <span style={{ color: theme.palette.error.main }}>
            {translate(error)}
          </span>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button label="ra.action.cancel" onClick={handleClose} />
        <Button label="ra.action.add" onClick={handleConfirm} />
      </DialogActions>
    </Dialog>
  );

};

export const NotifiedPeopleInput = ({
  showCertifiedUserOption = false,
  label,
  helperText,
  isRequired,
}: NotifiedPeopleInputProps) => {

  const translate = useTranslate();
  const { getValues, setValue, watch } = useFormContext();
  const record = useRecordContext<{ notifiedPeople?: { emails?: string[] } }>();
  const watchedEmails = watch('notifiedPeople.emails') as string[] | undefined;

  const { data: users = [] } = useGetList('app-users', {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  }, { staleTime: LIST_DEBOUNCE });

  const choices = useMemo(() => {

    const result: NotifiedPersonChoice[] = [];
    const emailList = watchedEmails ?? record?.notifiedPeople?.emails ?? [];

    if (showCertifiedUserOption) {

      result.push({ id: CERTIFIED_USER_ID, type: 'certifiedUser' });

    }

    users.forEach((user) => {

      result.push({
        id: String(user.id),
        type: 'user',
        name: user.name ?? user.username ?? String(user.id),
      });

    });

    emailList.forEach((email: string) => {

      const choice = toEmailChoice(email);
      if (!result.some((item) => item.id === choice.id)) {

        result.push(choice);

      }

    });

    return result;

  }, [users, watchedEmails, record?.notifiedPeople?.emails, showCertifiedUserOption]);

  const choicesRef = useRef(choices);
  choicesRef.current = choices;

  const handleAddEmailChoice = (choice: NotifiedPersonChoice) => {

    if (choice.type !== 'email') return;

    if (!choicesRef.current.some((c) => c.id === choice.id)) {

      choicesRef.current = [...choicesRef.current, choice];

    }

    const currentEmails: string[] = getValues('notifiedPeople.emails') ?? [];
    if (!currentEmails.includes(choice.email)) {

      setValue('notifiedPeople.emails', [...currentEmails, choice.email], { shouldDirty: true });

    }

  };

  const getChoiceLabel = (choice: NotifiedPersonChoice) => {

    if (choice.type === 'certifiedUser') {

      return translate('resources.certification-categories.fields.certified_user');

    }
    if (choice.type === 'email') {

      return choice.email;

    }
    return choice.name;

  };

  const formatSelection = (peopleIds: string[] | undefined) => {

    const notifiedPeople = getValues('notifiedPeople') ?? {};
    const ids: string[] = [...(peopleIds ?? [])];

    if (showCertifiedUserOption && notifiedPeople.isCertificationHolderNotified) {

      if (!ids.includes(CERTIFIED_USER_ID)) {

        ids.unshift(CERTIFIED_USER_ID);

      }

    }

    (notifiedPeople.emails ?? []).forEach((email: string) => {

      const emailId = `email:${email}`;
      if (!ids.includes(emailId)) {

        ids.push(emailId);

      }

    });

    return ids;

  };

  const parseSelection = (selectedIds: string[] | undefined) => {

    const selectedChoices = (selectedIds ?? [])
      .map((id) => choicesRef.current.find((choice) => choice.id === id))
      .filter((choice): choice is NotifiedPersonChoice => choice !== undefined);

    if (showCertifiedUserOption) {

      setValue(
        'notifiedPeople.isCertificationHolderNotified',
        selectedChoices.some((choice) => choice.type === 'certifiedUser'),
        { shouldDirty: true },
      );

    }

    setValue(
      'notifiedPeople.emails',
      selectedChoices
        .filter((choice): choice is Extract<NotifiedPersonChoice, { type: 'email' }> => choice.type === 'email')
        .map((choice) => choice.email),
      { shouldDirty: true },
    );

    return selectedChoices
      .filter((choice): choice is Extract<NotifiedPersonChoice, { type: 'user' }> => choice.type === 'user')
      .map((choice) => choice.id);

  };

  return (
    <GrayAutocompleteArrayInputWithUpperLabel
      source="notifiedPeople.peopleToNotify"
      forcePopupIcon
      label={label}
      helperText={helperText}
      choices={choices}
      optionValue="id"
      isRequired={isRequired}
      TextFieldProps={{
        FormHelperTextProps: {
          sx: {
            margin: 0,
            marginLeft: 0,
          },
        },
      }}
      optionText={(choice: NotifiedPersonChoice) => getChoiceLabel(choice)}
      inputText={(choice: NotifiedPersonChoice) => getChoiceLabel(choice)}
      matchSuggestion={(filter, choice: NotifiedPersonChoice) => getChoiceLabel(choice)
        .toLowerCase()
        .includes(filter.toLowerCase())}
      format={formatSelection}
      parse={parseSelection}
      renderTags={(value, getTagProps) => (value as NotifiedPersonChoice[]).map(
        (option: NotifiedPersonChoice, index: number) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={getChoiceLabel(option)}
            size="small"
          />
        ),
      )}
      createItemLabel=""
      createLabel={translate('general.text.add_email')}
      create={
        <EmailCreateDialog onAddChoice={handleAddEmailChoice} />
      }
      componentsProps={{
        popper: {
          sx: { zIndex: 1200 },
        },
        paper: {
          sx: {
            '& .MuiAutocomplete-option:last-of-type': {
              justifyContent: 'center',
              textAlign: 'center',
              bgcolor: '#E5F1EA',
              borderRadius: '3px',
              '&:hover': {
                bgcolor: '#ACB7B1',
              },
            },
          },
        },
      }}
    />
  );

};
